import { UserModulePermissions } from './user';

export type PackageType = 'free' | 'basic' | 'pro' | 'enterprise' | 'custom';

export interface PackageModule {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'crm' | 'products' | 'sales' | 'documents' | 'projects' | 'finance' | 'admin' | 'core';
  isCore: boolean; // Core modulok minden csomagban benne vannak
  permissions: UserModulePermissions[keyof UserModulePermissions]; // 'none' | 'access' | 'admin'
}

export interface Package {
  id: string;
  name: string;
  description: string;
  type: PackageType;
  price: {
    monthly: number;
    yearly: number;
    currency: string;
  };
  maxUsers: number;
  maxOrganizations: number;
  maxStorage: number; // GB-ban
  features: string[];
  modules: string[]; // Module ID-k
  isActive: boolean;
  isPopular?: boolean;
  customModules?: string[]; // Custom csomagokhoz
  createdAt: Date;
  updatedAt: Date;
}

export interface OrganizationSubscription {
  id: string;
  organizationId: string;
  packageId: string;
  status: 'active' | 'inactive' | 'suspended' | 'cancelled' | 'trial';
  startDate: Date;
  endDate?: Date;
  trialEndDate?: Date;
  autoRenew: boolean;
  paymentMethod?: string;
  lastPaymentDate?: Date;
  nextPaymentDate?: Date;
  customModules?: string[]; // Egyedi modulok
  usage: {
    users: number;
    storage: number; // GB-ban
    apiCalls: number;
    lastUpdated: Date;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface ModuleUsageLimit {
  moduleId: string;
  maxRecords?: number;
  maxApiCalls?: number;
  maxStorage?: number;
  features: string[];
}

// Előre definiált csomagok
export const PREDEFINED_PACKAGES: Omit<Package, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Ingyenes',
    description: 'Alapvető CRM funkciók kis csapatoknak',
    type: 'free',
    price: {
      monthly: 0,
      yearly: 0,
      currency: 'HUF'
    },
    maxUsers: 2,
    maxOrganizations: 1,
    maxStorage: 1,
    features: [
      'Alapvető ügyfélkezelés',
      'Egyszerű termékkezelés',
      'Email támogatás',
      '1 GB tárhely'
    ],
    modules: ['customers', 'products', 'settings'],
    isActive: true
  },
  {
    name: 'Alap',
    description: 'Teljes CRM funkciók kis és közepes vállalkozásoknak',
    type: 'basic',
    price: {
      monthly: 9900,
      yearly: 99000,
      currency: 'HUF'
    },
    maxUsers: 10,
    maxOrganizations: 1,
    maxStorage: 10,
    features: [
      'Teljes ügyfélkezelés',
      'Termék és készletkezelés',
      'Ajánlatok és megrendelések',
      'Alapvető jelentések',
      'Email integráció',
      '10 GB tárhely'
    ],
    modules: ['customers', 'suppliers', 'products', 'inventory', 'quotations', 'templates', 'forms', 'settings'],
    isActive: true
  },
  {
    name: 'Professzionális',
    description: 'Fejlett funkciók növekvő vállalkozásoknak',
    type: 'pro',
    price: {
      monthly: 69990,
      yearly: 839880,
      currency: 'HUF'
    },
    maxUsers: 50,
    maxOrganizations: 3,
    maxStorage: 50,
    features: [
      'Minden Alap csomag funkció',
      'Fejlett jelentések és analitika',
      'Projektkezelés és feladatok',
      'Dokumentum sablonok',
      'API hozzáférés',
      'Prioritás támogatás',
      '50 GB tárhely'
    ],
    modules: [
      'customers', 'suppliers', 'products', 'inventory', 'quotations', 'deals', 
      'contracts', 'templates', 'forms', 'worksheets', 'projects', 'tasks', 
      'billing', 'reports', 'settings'
    ],
    isActive: true,
    isPopular: true
  },
  {
    name: 'Vállalati',
    description: 'Teljes funkcionalitás nagy szervezeteknek',
    type: 'enterprise',
    price: {
      monthly: 150000,
      yearly: 1800000,
      currency: 'HUF'
    },
    maxUsers: -1, // Korlátlan
    maxOrganizations: -1, // Korlátlan
    maxStorage: 500,
    features: [
      'Minden Professzionális csomag funkció',
      'Korlátlan felhasználók',
      'Korlátlan szervezetek',
      'Egyedi integráció',
      'Dedikált támogatás',
      'SLA garancia',
      '500 GB tárhely',
      'Egyedi modulok'
    ],
    modules: [
      'customers', 'suppliers', 'products', 'inventory', 'quotations', 'deals', 
      'contracts', 'templates', 'forms', 'worksheets', 'projects', 'tasks', 
      'billing', 'reports', 'settings', 'admin'
    ],
    isActive: true
  }
];

// Modulok definíciója - TELJES LISTA a navigációnak megfelelően
export const AVAILABLE_MODULES: PackageModule[] = [
  // Core modulok - mindig elérhetők
  {
    id: 'customers',
    name: 'Ügyfélkezelés',
    description: 'Ügyfelek és kapcsolatok kezelése',
    icon: 'UserGroupIcon',
    category: 'core',
    isCore: true,
    permissions: 'access'
  },
  {
    id: 'settings',
    name: 'Beállítások',
    description: 'Rendszer és felhasználói beállítások',
    icon: 'Cog6ToothIcon',
    category: 'core',
    isCore: true,
    permissions: 'access'
  },

  // CRM modulok
  {
    id: 'suppliers',
    name: 'Beszállítók',
    description: 'Beszállítók és partnerek kezelése',
    icon: 'BuildingStorefrontIcon',
    category: 'crm',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'notes',
    name: 'Jegyzetek',
    description: 'Cetlik és jegyzetek kezelése',
    icon: 'DocumentTextIcon',
    category: 'crm',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'appointments',
    name: 'Időpontfoglalás',
    description: 'Találkozók és időpontok kezelése',
    icon: 'CalendarIcon',
    category: 'crm',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'customerPortal',
    name: 'Ügyfélportál',
    description: 'Ügyfél önkiszolgáló felület',
    icon: 'UserCircleIcon',
    category: 'crm',
    isCore: false,
    permissions: 'access'
  },

  // Termék modulok
  {
    id: 'products',
    name: 'Termékkezelés',
    description: 'Termékek és szolgáltatások kezelése',
    icon: 'CubeIcon',
    category: 'products',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'inventory',
    name: 'Készletkezelés',
    description: 'Raktár és készlet nyilvántartás',
    icon: 'ClipboardDocumentListIcon',
    category: 'products',
    isCore: false,
    permissions: 'access'
  },

  // Értékesítés modulok
  {
    id: 'quotations',
    name: 'Ajánlatok',
    description: 'Ajánlatok és megrendelések kezelése',
    icon: 'DocumentTextIcon',
    category: 'sales',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'deals',
    name: 'Üzletek',
    description: 'Értékesítési folyamatok és pipeline',
    icon: 'CurrencyDollarIcon',
    category: 'sales',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'sales',
    name: 'Értékesítés',
    description: 'Értékesítési adatok és statisztikák',
    icon: 'ShoppingCartIcon',
    category: 'sales',
    isCore: false,
    permissions: 'access'
  },

  // Dokumentum modulok
  {
    id: 'contracts',
    name: 'Szerződések',
    description: 'Szerződések és jogi dokumentumok',
    icon: 'DocumentTextIcon',
    category: 'documents',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'templates',
    name: 'Sablonok',
    description: 'Dokumentum és email sablonok',
    icon: 'DocumentTextIcon',
    category: 'documents',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'forms',
    name: 'Űrlapok',
    description: 'Egyedi űrlapok és adatgyűjtés (Form Builder)',
    icon: 'RectangleStackIcon',
    category: 'documents',
    isCore: false,
    permissions: 'access'
  },

  // Projekt modulok
  {
    id: 'projects',
    name: 'Projektek',
    description: 'Projektkezelés és nyomon követés',
    icon: 'ChartBarIcon',
    category: 'projects',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'tasks',
    name: 'Feladatok',
    description: 'Feladat és teendő kezelés',
    icon: 'ClipboardDocumentListIcon',
    category: 'projects',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'worksheets',
    name: 'Munkalapok',
    description: 'Időnyilvántartás és munkalapok',
    icon: 'ClipboardDocumentListIcon',
    category: 'projects',
    isCore: false,
    permissions: 'access'
  },

  // Pénzügy modulok
  {
    id: 'billing',
    name: 'Számlázás',
    description: 'Számlák és pénzügyi kezelés',
    icon: 'ReceiptPercentIcon',
    category: 'finance',
    isCore: false,
    permissions: 'access'
  },
  {
    id: 'reports',
    name: 'Jelentések',
    description: 'Analitika és jelentések',
    icon: 'ChartBarIcon',
    category: 'finance',
    isCore: false,
    permissions: 'access'
  },

  // Admin modulok
  {
    id: 'admin',
    name: 'Rendszergazda',
    description: 'Rendszer adminisztráció',
    icon: 'ShieldCheckIcon',
    category: 'admin',
    isCore: false,
    permissions: 'admin'
  }
];
